<template>
    <nav :class="[{'injectable-mobile': isInjectable}]" class="navbar navbar--border navbar--height navbar--mobile" role="navigation" aria-label="main navigation">
        <ul class="navbar-list--inline">
            <template v-if="!isCheckout && !isInjectable">
				<div class="navbar-item">
					<div class="back-button-wrapper" v-if="displayBackButton()">
						<BaseBackButton
							:useTitle="true"
							title="Back"
							:click="handleBackButtonClick" />
					</div>
				</div>
				<template v-if="main()">
                    <div class="navigation-title" :class="centerNavigationStyle">
                        <router-link class="logo-container" to="/">
                            <img alt="store logo image" class="logo" src="../../assets/store-logo.png"/>
                        </router-link>
                        <navigation-location-mobile />
                    </div>
                </template>
				<template v-if="!main()">
					<div class="navigation-title" :class="centerNavigationStyle">
						<div class="title-container" :class="setResponsiveTitle" v-if="hasTitle()">
							{{ titleName }}
						</div>
					</div>
				</template>
				<account-menu />
            </template>

            <template v-if="isInjectable">
                <div v-if="!order" class="columns is-mobile full-width">
                    <div class="column is-8">
                        <p v-if="customer">
                            Hi <span class="store-navigation-color">{{customer.firstName}}</span>!<br>
                            <template v-if="loyaltyPoints">You have <span class="store-navigation-color">{{loyaltyPoints}} {{ $appConstants.pointsName }}</span></template>
                        </p>
                    </div>

                    <div class="column is-4 is-narrow">
                        <navigation-route  target="_parent" 
                            :link="baseURL + ((itemCount) ? 'checkout' : '')"
                            :isVueRoute="false"
                            classes="button is-primary is-rounded nav-btn is-pulled-right"
                            >
                               {{(itemCount) ? 'Checkout' : 'Start your order'}}
                                <span class="checkout-arrow"><BaseIcon 
                                    icon="forwardArrow" 
                                    size="sm" 
                                    v-if="!itemCount"
                                    /></span>
                                <div v-if="itemCount" class="item-count-background">
                                    <span class="item-count">
                                        {{ itemCount }}
                                    </span>
                                </div>
                        </navigation-route> 
                    </div>
                </div>

                <div class="header-tracker" v-else>
                    <order-tracker
                        :orderLocation="location"
                        :order="order"
                        :isStreamLined="true"
                    />
                </div>

            </template>
        </ul>
		<slot></slot>
    </nav>
</template>

<script>
import { ACCOUNT, RECENT_ORDERS, CART, LOCATION } from "@/stores/defs";
import { mapGetters } from "vuex";
import CheckoutButton from "./CheckoutButton.vue";
import AccountMenu from "@NavItem/AccountMenu.vue";
import DateManipulator from '@/utils/DateManipulator';
import NavigationLocationMobile from '@/components/header/NavigationLocationMobile.vue';
import NavigationRoute from "@/components/header/NavigationRoute";
import orderService from "@/services/order.service";
import OrderTracker from "@/components/orders/OrderTracker";
import recentOrderService from "@/services/recent.orders.service";
import SlideoutMenu from "./SlideoutMenu.vue";
import { reverseVanityName } from '@/utils/helper.functions';
import { ResponsiveMixin } from "vue-responsive-components";
import flowStateMixin from '@Mixin/flowStateMixin';

export default {
	mixins: [ResponsiveMixin, flowStateMixin],
	breakpoints: {
		compactTablet: (el) => {
			return (el.width >= 700 && el.width <= 1088);
		},
	},
	components: {
		"checkout-button": CheckoutButton,
		"slideout-menu": SlideoutMenu,
		"navigation-route": NavigationRoute,
		"order-tracker": OrderTracker,
		'navigation-location-mobile': NavigationLocationMobile,
		SlideoutMenu,
		"account-menu": AccountMenu,
	},
    computed: {
        isCheckout() {
            return this.$route.meta.isCheckout;
        },
        isInjectable() {
            return this.$route.name === "injectable-header";
        },
        ...mapGetters({
            customer: ACCOUNT.GET_CUSTOMER,
            recentOrders: RECENT_ORDERS.GET_RECENT_ORDERS,
            itemCount: CART.ITEM_COUNT,
            location: LOCATION.GET_LOCATION,
            loyaltyPoints: ACCOUNT.GET_LOYALTY,
            loggedIn: ACCOUNT.GET_LOGGED_IN
        }),
        baseURL() {
            if (this.isInjectable) {
                return process.env.BASE_URL;
            } else {
                return "/";
            }
		},
		setResponsiveTitle() {
			if (this.el.is.compactTablet) {
				return 'large-title';
			}
			return '';
		},
		centerNavigationStyle() {
			const exitedFlow = this.exitedAllFlows;
			if (!exitedFlow) {
				return 'hide';
			}
			return '';
		},
    },
    methods: {
        displayBackButton() {
			const route = this.$route.name;
			const lastFlow = this.getLastActiveFlow();
			const exitedAll = this.exitedAllFlows;
			if (lastFlow && !exitedAll) {
				return true;
			}
            if (route === 'home') {
                return false;
            }
            return true;
		},
		handleBackButtonClick() {
			const exitedFlow = this.exitedAllFlows;
			if (exitedFlow) {
				return null;
			}
			const hasPreviousFlowState = this.resetLastActiveFlow();
			if (!hasPreviousFlowState) {
				return null;
			}
		},
        main: function() {
            return this.$route.name === "home";
        },
        getActiveOrder() {
            if (this.isInjectable) {
                if (this.customer && this.recentOrders.length) {
                    if (this.recentOrders[0] &&  DateManipulator.isMomentToday(this.recentOrders[0].purchaseDate)) {
                        orderService
                            .getOrder(this.recentOrders[0].id, this.recentOrders[0].locationBase)
                            .then(res => {
                                this.order = recentOrderService.convertSingleOrder(res.data.body);
                            })
                            .catch(err => {
                                this.order = null;
                                console.log(err);
                            });
                    }
                } else {
                    this.order = null;
                }
            } else {
                this.order = null;
            }
		},
		hasTitle() {
			const route = this.$route;
			const routeName = route.name;
			if (routeName === 'item-detail') {
				const paramItem = route.params.item;
				const paramName = route.params.name;
				if (paramItem) {
					this.titleName = paramItem.name;
				} else {
					this.titleName = reverseVanityName(paramName);
				}
			} else if (routeName === 'category') {
				this.titleName = reverseVanityName(route.params.categoryId);
			} else {
				this.titleName = null;
			}
			return this.titleName;
		}
    },
    data() {
        return {
			order: null,
			titleName: null,
        };
    },
    mounted() {
        if (!this.order && this.isInjectable) {
            this.getActiveOrder();
        }
    },
    watch: {
        recentOrders: {
            handler(newVal, oldVal) {
                if (newVal && this.isInjectable) {
                    this.getActiveOrder();
                }
            },
            deep: true
        }
    }
};
</script>

<style lang="scss">
@import 'commonStyle/navbar';
</style>


// WEBPACK FOOTER //
// src/components/header/NavigationMobile.vue